import type { AptiErrorCode } from "./codes";

/**
 * Base error class for all AptiHealth application errors
 *
 * @example
 * ```typescript
 * throw new AptiError({
 *   cause: axiosError,
 *   code: "S3_UPLOAD_FAILED",
 *   context: {
 *     fileSize: 2048576,
 *   },
 *   message: "Failed to upload Personal ID to S3",
 * });
 * ```
 */

export type AptiErrorParams = {
  /** Optional original error that caused this error */
  cause?: unknown;
  /** Error code identifying the error type */
  code: AptiErrorCode;
  /** Optional metadata for debugging (no PHI/PII) */
  context?: Record<string, unknown>;
  /** Human-readable error message */
  message: string;
  /** Optional HTTP status code hint */
  statusCode?: number;
};

export class AptiError extends Error {
  public readonly cause: unknown;
  public readonly code: AptiErrorCode;
  public readonly context: Record<string, unknown>;
  public readonly statusCode: number;

  constructor(params: AptiErrorParams) {
    super(params.message);

    this.name = this.constructor.name;
    this.cause = params.cause;
    this.code = params.code;
    this.context = params.context;
    this.statusCode = params.statusCode;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}
